import { useMemo, useState } from 'react';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Modal,
  TextField,
  Select,
  Checkbox,
  Badge,
  Toast,
  Frame,
  Divider,
  DataTable,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { useShop } from '../hooks/useShop';
import { useCurrency } from '../hooks/useCurrency';
import { client } from '../api/client';
import { Endpoints } from '../api/endpoints';

interface Alert {
  id: number;
  name: string | null;
  metric: string;
  operator: string;
  threshold: number | string;
  window_hours: number;
  cooldown_hours: number;
  notify_email: boolean;
  email: string | null;
  notify_slack: boolean;
  slack_webhook: string | null;
  notify_webhook: boolean;
  webhook_url: string | null;
  is_active: boolean;
  last_triggered_at: string | null;
  trigger_count: number;
}

interface AlertEvent {
  id: number;
  triggered_at: string;
  metric_value: number | string;
  message: string | null;
  notified_channels: string[] | null;
}

const METRICS = [
  { label: 'Net margin', value: 'net_margin' },
  { label: 'Net profit', value: 'net_profit' },
  { label: 'Revenue', value: 'revenue' },
  { label: 'ROAS', value: 'roas' },
  { label: 'CPA (spend / order)', value: 'cpa' },
  { label: 'AOV (revenue / order)', value: 'aov' },
];
const METRIC_LABEL: Record<string, string> = Object.fromEntries(METRICS.map((m) => [m.value, m.label]));

const OPERATORS = [
  { label: 'is below (<)', value: 'lt' },
  { label: 'is below or equal (≤)', value: 'lte' },
  { label: 'is above (>)', value: 'gt' },
  { label: 'is above or equal (≥)', value: 'gte' },
  { label: 'drops by ≥ (%)', value: 'pct_drop' },
];
const OP_SYMBOL: Record<string, string> = { lt: '<', lte: '≤', gt: '>', gte: '≥', pct_drop: 'drop ≥' };

type FormState = {
  name: string;
  metric: string;
  operator: string;
  threshold: string;
  window_hours: string;
  cooldown_hours: string;
  notify_email: boolean;
  email: string;
  notify_slack: boolean;
  slack_webhook: string;
  notify_webhook: boolean;
  webhook_url: string;
  is_active: boolean;
};

const BLANK: FormState = {
  name: '',
  metric: 'net_margin',
  operator: 'lt',
  threshold: '0.05',
  window_hours: '24',
  cooldown_hours: '24',
  notify_email: true,
  email: '',
  notify_slack: false,
  slack_webhook: '',
  notify_webhook: false,
  webhook_url: '',
  is_active: true,
};

function thresholdHelp(metric: string, operator: string): string {
  if (operator === 'pct_drop') return 'Percent drop vs the previous equal window, e.g. 20 = 20%.';
  if (metric === 'net_margin') return 'Fraction, e.g. 0.05 = 5%.';
  if (metric === 'roas') return 'Ratio, e.g. 2 = 2×.';
  return 'Amount in your store currency.';
}

export default function Alerts() {
  const { shop } = useShop();
  const { formatMoney } = useCurrency(shop?.currency ?? 'USD');

  const alerts = useApi<{ data: Alert[] }>(Endpoints.alerts.list);
  const events = useApi<{ data: AlertEvent[] }>(Endpoints.alerts.events);
  const metrics = useApi<{ window_hours: number; values: Record<string, number | null> }>(
    Endpoints.alerts.metrics,
  );

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(BLANK);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const openNew = () => {
    setEditingId(null);
    setForm({ ...BLANK }); // email left blank → backend uses the store owner email
    setOpen(true);
  };

  const openEdit = (a: Alert) => {
    setEditingId(a.id);
    setForm({
      name: a.name ?? '',
      metric: a.metric,
      operator: a.operator,
      threshold: String(a.threshold),
      window_hours: String(a.window_hours),
      cooldown_hours: String(a.cooldown_hours),
      notify_email: a.notify_email,
      email: a.email ?? '',
      notify_slack: a.notify_slack,
      slack_webhook: a.slack_webhook ?? '',
      notify_webhook: a.notify_webhook,
      webhook_url: a.webhook_url ?? '',
      is_active: a.is_active,
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name || null,
        metric: form.metric,
        operator: form.operator,
        threshold: parseFloat(form.threshold || '0'),
        window_hours: parseInt(form.window_hours || '24', 10),
        cooldown_hours: parseInt(form.cooldown_hours || '24', 10),
        notify_email: form.notify_email,
        email: form.email || null,
        notify_slack: form.notify_slack,
        slack_webhook: form.slack_webhook || null,
        notify_webhook: form.notify_webhook,
        webhook_url: form.webhook_url || null,
        is_active: form.is_active,
      };
      if (editingId) {
        await client.put(Endpoints.alerts.update(editingId), payload);
      } else {
        await client.post(Endpoints.alerts.create, payload);
      }
      setToast('Alert saved');
      setOpen(false);
      alerts.refetch();
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    await client.delete(Endpoints.alerts.remove(id));
    setToast('Alert deleted');
    alerts.refetch();
  };

  const toggleActive = async (a: Alert) => {
    await client.put(Endpoints.alerts.update(a.id), { is_active: !a.is_active });
    alerts.refetch();
  };

  const describe = (a: Alert) => {
    const t = Number(a.threshold);
    const val =
      a.operator === 'pct_drop'
        ? `${t}%`
        : a.metric === 'net_margin'
          ? `${(t * 100).toFixed(1)}%`
          : a.metric === 'roas'
            ? `${t}×`
            : formatMoney(t);
    return `${METRIC_LABEL[a.metric] ?? a.metric} ${OP_SYMBOL[a.operator] ?? a.operator} ${val} · last ${a.window_hours}h`;
  };

  const fmtMetric = (metric: string, v: number | null | undefined) => {
    if (v == null) return '—';
    if (metric === 'net_margin') return `${(v * 100).toFixed(1)}%`;
    if (metric === 'roas') return `${v.toFixed(2)}×`;
    return formatMoney(v);
  };

  const alertRows = useMemo(
    () =>
      (alerts.data?.data ?? []).map((a) => [
        a.name || METRIC_LABEL[a.metric] || a.metric,
        describe(a),
        [a.notify_email && 'Email', a.notify_slack && 'Slack', a.notify_webhook && 'Webhook']
          .filter(Boolean)
          .join(', ') || '—',
        a.is_active ? <Badge tone="success">Active</Badge> : <Badge>Paused</Badge>,
        a.last_triggered_at ? `${a.last_triggered_at.slice(0, 16).replace('T', ' ')} (${a.trigger_count}×)` : '—',
        <InlineStack gap="200">
          <Button variant="plain" onClick={() => openEdit(a)}>
            Edit
          </Button>
          <Button variant="plain" onClick={() => toggleActive(a)}>
            {a.is_active ? 'Pause' : 'Resume'}
          </Button>
          <Button variant="plain" tone="critical" onClick={() => remove(a.id)}>
            Delete
          </Button>
        </InlineStack>,
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alerts.data, shop?.currency],
  );

  return (
    <Frame>
      <Page
        title="Profit alerts"
        subtitle="Get notified when a profit metric crosses a threshold"
        primaryAction={{ content: 'New alert', onAction: openNew }}
      >
        <BlockStack gap="400">
          <Card>
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                Current readings (last {metrics.data?.window_hours ?? 24}h)
              </Text>
              <InlineStack gap="500" wrap>
                {METRICS.map((m) => (
                  <BlockStack key={m.value} gap="050">
                    <Text as="span" variant="bodySm" tone="subdued">
                      {m.label}
                    </Text>
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      {fmtMetric(m.value, metrics.data?.values?.[m.value] ?? null)}
                    </Text>
                  </BlockStack>
                ))}
              </InlineStack>
            </BlockStack>
          </Card>

          <Card>
            {alertRows.length > 0 ? (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                headings={['Name', 'Condition', 'Channels', 'Status', 'Last fired', '']}
                rows={alertRows}
              />
            ) : (
              <Text as="p" variant="bodyMd" tone="subdued">
                No alerts yet. Create one to get notified when margin drops, ROAS falls, or costs spike.
              </Text>
            )}
          </Card>

          {(events.data?.data?.length ?? 0) > 0 && (
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">
                  Recent triggers
                </Text>
                {(events.data?.data ?? []).slice(0, 10).map((e) => (
                  <InlineStack key={e.id} gap="300" blockAlign="center" wrap>
                    <Text as="span" variant="bodySm" tone="subdued">
                      {e.triggered_at.slice(0, 16).replace('T', ' ')}
                    </Text>
                    <Text as="span" variant="bodyMd">
                      {e.message}
                    </Text>
                    {(e.notified_channels ?? []).map((c) => (
                      <Badge key={c} tone="info">
                        {c}
                      </Badge>
                    ))}
                  </InlineStack>
                ))}
              </BlockStack>
            </Card>
          )}
        </BlockStack>
      </Page>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editingId ? 'Edit alert' : 'New alert'}
        primaryAction={{ content: 'Save', onAction: save, loading: saving }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Name (optional)"
              value={form.name}
              onChange={(v) => set('name', v)}
              autoComplete="off"
              placeholder="e.g. Margin watch"
            />
            <InlineStack gap="300" wrap>
              <Select label="Metric" options={METRICS} value={form.metric} onChange={(v) => set('metric', v)} />
              <Select
                label="Condition"
                options={OPERATORS}
                value={form.operator}
                onChange={(v) => set('operator', v)}
              />
            </InlineStack>
            <InlineStack gap="300" wrap>
              <TextField
                label="Threshold"
                type="number"
                value={form.threshold}
                onChange={(v) => set('threshold', v)}
                autoComplete="off"
                helpText={thresholdHelp(form.metric, form.operator)}
              />
              <TextField
                label="Window (hours)"
                type="number"
                value={form.window_hours}
                onChange={(v) => set('window_hours', v)}
                autoComplete="off"
              />
              <TextField
                label="Cooldown (hours)"
                type="number"
                value={form.cooldown_hours}
                onChange={(v) => set('cooldown_hours', v)}
                autoComplete="off"
                helpText="Minimum gap between re-fires."
              />
            </InlineStack>

            <Divider />
            <Text as="h3" variant="headingSm">
              Notify via
            </Text>
            <Checkbox label="Email" checked={form.notify_email} onChange={(v) => set('notify_email', v)} />
            {form.notify_email && (
              <TextField
                label="Email address"
                type="email"
                value={form.email}
                onChange={(v) => set('email', v)}
                autoComplete="off"
                helpText="Leave blank to use your store owner email."
              />
            )}
            <Checkbox label="Slack" checked={form.notify_slack} onChange={(v) => set('notify_slack', v)} />
            {form.notify_slack && (
              <TextField
                label="Slack incoming webhook URL"
                value={form.slack_webhook}
                onChange={(v) => set('slack_webhook', v)}
                autoComplete="off"
                placeholder="https://hooks.slack.com/services/…"
              />
            )}
            <Checkbox label="Custom webhook" checked={form.notify_webhook} onChange={(v) => set('notify_webhook', v)} />
            {form.notify_webhook && (
              <TextField
                label="Webhook URL"
                value={form.webhook_url}
                onChange={(v) => set('webhook_url', v)}
                autoComplete="off"
                placeholder="https://…"
              />
            )}

            <Divider />
            <Checkbox label="Active" checked={form.is_active} onChange={(v) => set('is_active', v)} />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {toast && <Toast content={toast} onDismiss={() => setToast(null)} />}
    </Frame>
  );
}
